/* eslint-disable max-lines */
import { sleep } from "common/utils/helpers/delay";
import { EventBus, Events } from "jiffy-store";
import { on as onEvent } from "common/utils/ui/event";
import { readCookie } from "common/utils/cookie";
import { storage } from "common/utils/storage";
import { track } from "common/utils/mixpanel/tracker";
import UserContextHelper from "components/jiffy/masthead-redesign2025/local-delivery/utils/user-context-helper";

// Delay before publishing events to allow DOM updates to complete
const DELAY_MS = 100;
const STORAGE_KEY = "localDeliveryClosed";

const MIXPANEL_EVENTS = {
  OPENED: "local_delivery_modal_opened",
  CLOSED: "local_delivery_modal_closed",
  VIEWED: "address_collection_modal_viewed",
  SIGNIN_CLICKED: "address_modal_signin_clicked",
  SIGNUP_CLICKED: "address_modal_signup_clicked",
  DISMISSED: "address_collection_modal_dismissed"
};

const SELECTORS = {
  LOCAL_DELIVERY_ELM: ".js-local-delivery-modal",
  CLOSE_BTN: ".js-local-delivery-modal-close",
  CITY_ELM: ".js-local-delivery-modal-city",
  CTA_AS_INPUT: ".js-local-delivery-modal-address-input",
  SIGNIN_BTN: ".local-delivery-modal__signin-button",
  SIGNUP_BTN: ".local-delivery-modal__create-account-link"
};

export default class LocalDeliveryModal {
  #dismissalMethod = null;

  #isProgrammaticClose = false;

  #config = {
    closeOnBackdrop: true
  };

  /**
   * Initializes the local delivery modal component
   *
   * Sets up event listeners for user interactions.
   *
   * @param {Object} [config={}] - Configuration options
   * @param {boolean} [config.closeOnBackdrop=true] - Whether clicking backdrop closes modal
   * @constructor
   */
  constructor(config = {}) {
    this.#config = {
      ...this.#config,
      ...config
    };
    this.#bindEvents();
  }

  // eslint-disable-next-line class-methods-use-this
  get #$localDevliery() {
    return document.querySelector(SELECTORS.LOCAL_DELIVERY_ELM);
  }

  get #$closeBtns() {
    return this.#$localDevliery?.querySelectorAll(SELECTORS.CLOSE_BTN);
  }

  get #$cityElement() {
    return this.#$localDevliery?.querySelector(SELECTORS.CITY_ELM);
  }

  get #$ctaAsInput() {
    return this.#$localDevliery?.querySelector(SELECTORS.CTA_AS_INPUT);
  }

  /**
   * Attaches event listeners to interactive elements
   *
   * Close buttons: Hides the modal and persists the closed state
   * Dialog close event: Detects ESC key and handles cleanup
   * Backdrop clicks: Only if closeOnBackdrop config is enabled
   * Action buttons: Address input CTA, sign-in, and sign-up buttons
   *
   * @private
   */
  #bindEvents() {
    this.#bindCloseButtons();
    this.#bindDialogCloseEvent();
    this.#bindBackdropClick();
    this.#bindActionButtons();
  }

  /**
   * Binds close button event listeners
   * @private
   */
  #bindCloseButtons() {
    this.#$closeBtns?.forEach($closeBtn => {
      $closeBtn.addEventListener("click", e => {
        e.preventDefault();
        this.hideModal("close_button");
      });
    });
  }

  /**
   * Binds native dialog close event (ESC key)
   * @private
   */
  #bindDialogCloseEvent() {
    this.#$localDevliery?.addEventListener("close", () => {
      // Only handle if not already processing a close
      if (!this.#dismissalMethod && !this.#isProgrammaticClose) {
        // User pressed ESC - dialog already closed by browser, just do cleanup
        this.#dismissalMethod = "esc_key";
        this.#trackModalDismissed("esc_key", "collection_modal");
        this.#releaseScroll();
        storage.set(STORAGE_KEY, true);
        this.#trackModalEvent(MIXPANEL_EVENTS.CLOSED);
        sleep(DELAY_MS).then(() => EventBus.publish(Events.LOCAL_DELIVERY_MODAL_CLOSED, this));
      }
      // Reset flags after handling close event
      this.#dismissalMethod = null;
      this.#isProgrammaticClose = false;
    });
  }

  /**
   * Binds backdrop click event if enabled
   * @private
   */
  #bindBackdropClick() {
    if (this.#config.closeOnBackdrop) {
      this.#$localDevliery?.addEventListener("click", e => {
        if (e.target === this.#$localDevliery) {
          this.hideModal("outside_click");
        }
      });
    }
  }

  /**
   * Binds action button event listeners
   * @private
   */
  #bindActionButtons() {
    onEvent("click", SELECTORS.CTA_AS_INPUT, e => {
      e.preventDefault();
      EventBus.publish(Events.SHOW_SS_AUTOCOMPLETE_PANEL, this);
    });

    onEvent("click", SELECTORS.SIGNIN_BTN, e => {
      const source = e.target.closest(SELECTORS.SIGNIN_BTN)?.dataset?.source || "collection_modal";
      this.#trackSignInClicked(source);
    });

    onEvent("click", SELECTORS.SIGNUP_BTN, () => {
      this.#trackSignUpClicked();
    });
  }

  /**
   * Sends modal interaction event to Mixpanel analytics
   *
   * Includes the current city name (if available) to provide context
   * about which location the user is interacting with.
   *
   * @param {string} eventName - The Mixpanel event name to track
   * @private
   */
  #trackModalEvent(eventName) {
    track(eventName, {
      city: this.#$cityElement?.textContent || "NA"
    });
  }

  /**
   * Tracks when the address collection modal is viewed
   *
   * Sends analytics event with user type and zipcode to understand
   * modal display patterns and user engagement
   *
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackAddressCollectionModalViewed() {
    const zipcode = UserContextHelper.getSessionZipcode();
    const userType = UserContextHelper.getModalUserType();

    track(MIXPANEL_EVENTS.VIEWED, {
      user_type: userType,
      zipcode
    });
  }

  /**
   * Tracks when user clicks Sign-in button
   *
   * Sends analytics event with zipcode and source to understand user behavior
   * and conversion to sign-in flow from different modal contexts
   *
   * @param {string} source - Source of the click: "collection_modal" | "address_list_modal"
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackSignInClicked(source) {
    const zipcode = UserContextHelper.getSessionZipcode();

    track(MIXPANEL_EVENTS.SIGNIN_CLICKED, {
      zipcode,
      source
    });
  }

  /**
   * Tracks when user clicks Sign-up button from the modal
   *
   * Sends analytics event with zipcode to understand user behavior
   * and conversion from modal to sign-up flow
   *
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackSignUpClicked() {
    const zipcode = UserContextHelper.getSessionZipcode();

    track(MIXPANEL_EVENTS.SIGNUP_CLICKED, {
      zipcode
    });
  }

  /**
   * Tracks when modal is dismissed without entering address
   *
   * @param {string} dismissalMethod - How the modal was dismissed
   * @param {string} source - Which modal was dismissed: "collection_modal" | "address_list_modal"
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackModalDismissed(dismissalMethod, source = "collection_modal") {
    const zipcode = UserContextHelper.getSessionZipcode();

    track(MIXPANEL_EVENTS.DISMISSED, {
      dismissal_method: dismissalMethod,
      zipcode,
      source
    });
  }

  /**
   * Displays the local delivery modal dialog
   *
   * Shows the native dialog element, populates the city name from the address
   * cookie, tracks the open event in analytics, and publishes an event to notify
   * other components after a brief delay to ensure DOM updates are complete.
   *
   * @public
   */
  showModal() {
    if (!this.#$localDevliery) return;

    // Reset dismissal tracking for new modal display
    this.#dismissalMethod = null;

    this.#$localDevliery.showModal();
    this.#setCityName();
    this.#trackModalEvent(MIXPANEL_EVENTS.OPENED);
    this.#trackAddressCollectionModalViewed();
    this.#lockScroll();

    sleep(DELAY_MS).then(() => EventBus.publish(Events.LOCAL_DELIVERY_MODAL_OPENED, this));
  }

  /**
   * Locks the document scroll
   * Adds the "locked-all" class to the document element to prevent scrolling
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #lockScroll() {
    document.documentElement.classList.add("locked-all");
  }

  /**
   * Releases the document scroll
   * Removes the "locked-all" class from the document element to allow scrolling
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #releaseScroll() {
    document.documentElement.classList.remove("locked-all");
  }

  /**
   * Determines whether the modal should be displayed to the user
   *
   * Checks two conditions:
   * 1. Server-side flag (data-should-show attribute) indicates modal should appear
   * 2. User hasn't previously closed the modal in this session (checked via storage)
   *
   * This prevents repeatedly showing the modal to users who have already dismissed it.
   *
   * @returns {boolean} True if both conditions are met, false otherwise
   * @public
   */
  shouldShowModal() {
    if (!this.#$localDevliery) return false;

    const wasClosed = storage.get(STORAGE_KEY);
    if (wasClosed) return false;

    return this.#$localDevliery.getAttribute("data-should-show") === "true";
  }

  /**
   * Closes the local delivery modal dialog
   *
   * Hides the native dialog element, optionally persists the closed state to
   * storage (to prevent re-display), tracks the close event in analytics, and
   * publishes an event to notify other components.
   *
   * @param {string} [dismissalMethod=null] - How modal was dismissed: "close_button" | "outside_click" | "esc_key"
   * @param {boolean} [isStorageSet=true] - Whether to save closed state to storage.
   *                                        Set to false when closing programmatically
   *                                        without user action.
   * @public
   */
  hideModal(dismissalMethod = null, isStorageSet = true) {
    if (!this.#$localDevliery) return;

    // Mark as programmatic close if no dismissal method provided
    if (!dismissalMethod) {
      this.#isProgrammaticClose = true;
    }

    // Track dismissal if method provided
    if (dismissalMethod) {
      this.#dismissalMethod = dismissalMethod;
      this.#trackModalDismissed(dismissalMethod, "collection_modal");
    }

    this.#releaseScroll();

    this.#$localDevliery.close();
    if (isStorageSet) storage.set(STORAGE_KEY, true);
    this.#trackModalEvent(MIXPANEL_EVENTS.CLOSED);

    sleep(DELAY_MS).then(() => EventBus.publish(Events.LOCAL_DELIVERY_MODAL_CLOSED, this));
  }

  /**
   * Extracts and displays the city name from the address cookie
   *
   * Parses the address cookie which contains a URL-encoded address string
   * with parts separated by '+'. Filters out numeric-only parts (typically
   * zip codes) and reconstructs the city name from the remaining parts.
   *
   * Example: "New+York+10001" → "New York"
   *
   * Logs a warning if the city element or address cookie is missing, as this
   * indicates a configuration issue or unexpected state.
   *
   * @private
   */
  #setCityName() {
    let address = readCookie("address") ?? null;
    if (!this.#$cityElement || !address) {
      // eslint-disable-next-line no-console
      console.warn("City element or address cookie not found");
      return;
    }

    // Decode URL encoding if present (e.g., %20 → space)
    if (address.includes("%")) address = decodeURIComponent(address);

    const parts = address.split("+");
    // Filter out numeric parts (zip codes) to isolate city name components
    const cityParts = parts.filter(part => !/^\d+$/.test(part));

    this.#$cityElement.textContent = cityParts.join(" ").trim();
  }
}
